import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
  onSnapshot,
  QuerySnapshot,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  FeatureRequest,
  FeatureRequestStatus,
  FeatureRequestPriority,
  FeatureRequestComment,
} from '@/models/feature-request.model';

const FEATURE_REQUESTS_COLLECTION = 'featureRequests';
const COMMENTS_COLLECTION = 'featureRequestComments';

export class FeatureRequestService {
  /**
   * Get all feature requests
   */
  static async getAllFeatureRequests(): Promise<FeatureRequest[]> {
    const q = query(
      collection(db, FEATURE_REQUESTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return Promise.all(
      snapshot.docs.map(doc => this.docToFeatureRequest(doc.id, doc.data()))
    );
  }

  /**
   * Get feature request by ID
   */
  static async getFeatureRequestById(id: string): Promise<FeatureRequest | null> {
    const docRef = doc(db, FEATURE_REQUESTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    const featureRequest = await this.docToFeatureRequest(docSnap.id, docSnap.data());
    
    // Load comments
    const commentsQuery = query(
      collection(db, COMMENTS_COLLECTION),
      where('featureRequestId', '==', id),
      orderBy('createdAt', 'asc')
    );
    const commentsSnapshot = await getDocs(commentsQuery);
    featureRequest.comments = commentsSnapshot.docs.map(doc =>
      this.docToComment(doc.id, doc.data())
    );
    
    return featureRequest;
  }

  /**
   * Subscribe to feature requests changes
   */
  static subscribeToFeatureRequests(
    callback: (requests: FeatureRequest[]) => void
  ): () => void {
    const q = query(
      collection(db, FEATURE_REQUESTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, async (snapshot: QuerySnapshot) => {
      const requests = await Promise.all(
        snapshot.docs.map(doc => this.docToFeatureRequest(doc.id, doc.data()))
      );
      callback(requests);
    });
  }

  /**
   * Create feature request
   */
  static async createFeatureRequest(
    requestData: Partial<FeatureRequest>
  ): Promise<string> {
    const now = Timestamp.now();
    
    const data: any = {
      title: requestData.title || '',
      description: requestData.description || '',
      status: FeatureRequestStatus.submitted,
      priority: requestData.priority || FeatureRequestPriority.medium,
      submittedBy: requestData.submittedBy || '',
      submittedByName: requestData.submittedByName || null,
      submittedByEmail: requestData.submittedByEmail || null,
      submittedByRole: requestData.submittedByRole || null,
      votes: 0,
      voters: [],
      assignedTo: requestData.assignedTo || null,
      assignedToName: requestData.assignedToName || null,
      relatedTickets: requestData.relatedTickets || [],
      relatedFeatureRequests: requestData.relatedFeatureRequests || [],
      estimatedEffort: requestData.estimatedEffort || null,
      targetRelease: requestData.targetRelease || null,
      actualRelease: requestData.actualRelease || null,
      comments: [],
      tags: requestData.tags || [],
      category: requestData.category || null,
      metadata: requestData.metadata || {},
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, FEATURE_REQUESTS_COLLECTION), data);
    return docRef.id;
  }

  /**
   * Update feature request
   */
  static async updateFeatureRequest(
    requestId: string,
    updates: Partial<FeatureRequest>
  ): Promise<void> {
    const docRef = doc(db, FEATURE_REQUESTS_COLLECTION, requestId);
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.status !== undefined) {
      updateData.status = updates.status;
      if (updates.status === FeatureRequestStatus.completed && !updates.completedAt) {
        updateData.completedAt = Timestamp.now();
      }
    }
    if (updates.priority !== undefined) updateData.priority = updates.priority;
    if (updates.assignedTo !== undefined) updateData.assignedTo = updates.assignedTo;
    if (updates.assignedToName !== undefined) updateData.assignedToName = updates.assignedToName;
    if (updates.estimatedEffort !== undefined) updateData.estimatedEffort = updates.estimatedEffort;
    if (updates.targetRelease !== undefined) updateData.targetRelease = updates.targetRelease;
    if (updates.actualRelease !== undefined) updateData.actualRelease = updates.actualRelease;
    if (updates.tags !== undefined) updateData.tags = updates.tags;
    if (updates.category !== undefined) updateData.category = updates.category;

    await updateDoc(docRef, updateData);
  }

  /**
   * Vote for feature request
   */
  static async voteForFeatureRequest(
    requestId: string,
    userId: string
  ): Promise<void> {
    const docRef = doc(db, FEATURE_REQUESTS_COLLECTION, requestId);
    const request = await this.getFeatureRequestById(requestId);
    
    if (!request) throw new Error('Feature request not found');
    
    if (request.voters.includes(userId)) {
      // Remove vote
      await updateDoc(docRef, {
        votes: request.votes - 1,
        voters: arrayRemove(userId),
      });
    } else {
      // Add vote
      await updateDoc(docRef, {
        votes: request.votes + 1,
        voters: arrayUnion(userId),
      });
    }
  }

  /**
   * Add comment to feature request
   */
  static async addComment(
    requestId: string,
    comment: Omit<FeatureRequestComment, 'id' | 'featureRequestId' | 'createdAt'>
  ): Promise<string> {
    const data: any = {
      featureRequestId: requestId,
      authorId: comment.authorId,
      authorName: comment.authorName,
      authorEmail: comment.authorEmail || null,
      isInternal: comment.isInternal || false,
      content: comment.content,
      createdAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, COMMENTS_COLLECTION), data);
    return docRef.id;
  }

  /**
   * Convert Firestore document to FeatureRequest
   */
  private static async docToFeatureRequest(id: string, data: any): Promise<FeatureRequest> {
    return {
      id,
      title: data.title || '',
      description: data.description || '',
      status: data.status || FeatureRequestStatus.submitted,
      priority: data.priority || FeatureRequestPriority.medium,
      submittedBy: data.submittedBy || '',
      submittedByName: data.submittedByName,
      submittedByEmail: data.submittedByEmail,
      submittedByRole: data.submittedByRole,
      votes: data.votes || 0,
      voters: data.voters || [],
      assignedTo: data.assignedTo,
      assignedToName: data.assignedToName,
      relatedTickets: data.relatedTickets || [],
      relatedFeatureRequests: data.relatedFeatureRequests || [],
      estimatedEffort: data.estimatedEffort,
      targetRelease: data.targetRelease,
      actualRelease: data.actualRelease,
      comments: [], // Will be loaded separately
      tags: data.tags || [],
      category: data.category,
      metadata: data.metadata || {},
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      completedAt: data.completedAt?.toDate(),
    };
  }

  /**
   * Convert Firestore document to FeatureRequestComment
   */
  private static docToComment(id: string, data: any): FeatureRequestComment {
    return {
      id,
      featureRequestId: data.featureRequestId || '',
      authorId: data.authorId || '',
      authorName: data.authorName || '',
      authorEmail: data.authorEmail,
      isInternal: data.isInternal || false,
      content: data.content || '',
      createdAt: data.createdAt?.toDate() || new Date(),
    };
  }
}

